import { FC } from "react";

import { Box } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";

import { FormMessage, FullWidthVStack } from "@components/common";
import Loader from "@components/Loader";
import { OptionButtonGroup } from "@components/OptionButton";
import { Desktop, Mobile } from "@lib/media";

import useOrderFieldsLoading from "../../context/useOrderFieldsLoading";
import useOrderFormik from "../../hooks/useOrderFormik";
import SectionTitle from "../common/SectionTitle";
import Icon from "@components/Icon";
import Message from "@components/Message";
import useOrderData from "../../context/useOrderData";
import {DeliveryMethod, OrderFields} from "../../constants";

type TParcelWeightsProps = {
    name: string;
};

const destinationCountryFieldName = OrderFields.DESTINATION_COUNTRY;
const packageSizeFieldName = OrderFields.PACKAGE_SIZE;
const deliveryMethodFieldName = OrderFields.DELIVERY_METHOD;

const ParcelWeight: FC<TParcelWeightsProps> = ({ name }) => {
    const { t } = useTranslation();

    const { values, errors } = useOrderFormik();

    const fieldError = errors[name];

    const { isFieldLoading } = useOrderFieldsLoading();
    const isLoading = isFieldLoading(name);

    const { weightRegulations } = useOrderData();
    const destinationCountry = values[destinationCountryFieldName]?.toLowerCase() || '';

    if (values[destinationCountryFieldName] && !weightRegulations?.includes(destinationCountry)) {
        return;
    }

    const deliveryMethod = values[deliveryMethodFieldName] || '';

    const packageWeights = [
        {
            label: t("order-package-parcel_weight-less-than-10kg"),
            value: "10",
            isDisabled: !['XS', 'S', 'M', 'L'].includes(values[packageSizeFieldName] || '') &&
                (deliveryMethod === DeliveryMethod.PudoToPudo || deliveryMethod === DeliveryMethod.DoorToPudo)
        },
        {
            label: t("order-package-parcel_weight-10-20kg"),
            value: "20",
            isDisabled: !['XS', 'S', 'M', 'L'].includes(values[packageSizeFieldName] || '') &&
                (deliveryMethod === DeliveryMethod.PudoToPudo || deliveryMethod === DeliveryMethod.DoorToPudo)
        },
        {
            label: t("order-package-parcel_weight-more-than-20kg"),
            value: "31.5",
            isDisabled:
                ['XS', 'S', 'M', 'L'].includes(values[packageSizeFieldName] || '') &&
                (deliveryMethod === DeliveryMethod.PudoToPudo || deliveryMethod === DeliveryMethod.DoorToPudo)
        }
    ];

    return (
        <Loader isLoading={isLoading}>
            <FullWidthVStack>
                <SectionTitle>{t("order-package-parcel_weight")}</SectionTitle>
                <Box>
                    <Mobile>
                        <OptionButtonGroup
                            name={name}
                            options={packageWeights}
                            lines={[{ length: 1 }]}
                        />
                    </Mobile>
                    <Desktop>
                        <OptionButtonGroup
                            name={name}
                            options={packageWeights}
                            lines={[{ length: 3 }]}
                        />
                    </Desktop>
                </Box>
                <Message align="start" icon={<Icon icon="info" size="1.25rem" />} sx={{ py: "0.7rem" }}>
                    <Box>{ t('order-package-parcel_weight_select')}</Box>
                </Message>
                {fieldError && <FormMessage message={fieldError} isInvalid />}
            </FullWidthVStack>
        </Loader>
    );
};
export default ParcelWeight;
